import * as XLSX from 'xlsx';

/**
 * Utility functions for importing samples from Excel spreadsheets
 */

/**
 * Parse Excel file and extract sample data
 * @param {File} file - Excel file to parse
 * @returns {Promise<Array>} Array of sample objects
 */
export const parseExcelFile = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        // Get the first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        resolve(jsonData);
      } catch (error) {
        reject(new Error(`Failed to parse Excel file: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * Validate imported sample data
 * @param {Array} samples - Array of sample objects from Excel
 * @returns {Object} { valid: boolean, errors: Array, validSamples: Array }
 */
export const validateImportedSamples = (samples) => {
  const errors = [];
  const validSamples = [];

  // Required columns mapping (Excel column name -> internal field)
  const columnMapping = {
    'Name': 'name',
    'Position': 'position',
    'Freeze Date': 'freezeDate',
    'Frozen By': 'frozenBy',
    'PI': 'pi',
    'Passage Number': 'passageNumber',
    'Cap Color': 'capColor',
    'Notes': 'notes'
  };

  // Required fields
  const requiredFields = ['Name', 'Position', 'Freeze Date', 'Frozen By', 'PI'];

  samples.forEach((sample, index) => {
    const rowNumber = index + 2; // +2 because Excel is 1-indexed and has header row
    const sampleErrors = [];

    // Check required fields
    requiredFields.forEach(field => {
      if (!sample[field] || String(sample[field]).trim() === '') {
        sampleErrors.push(`Row ${rowNumber}: Missing required field "${field}"`);
      }
    });

    // Validate position format (e.g., A1, B3, etc.)
    if (sample['Position']) {
      const position = String(sample['Position']).trim().toUpperCase();
      const positionRegex = /^[A-J]([1-9]|10)$/;
      if (!positionRegex.test(position)) {
        sampleErrors.push(`Row ${rowNumber}: Invalid position "${position}". Must be A-J followed by 1-10 (e.g., A1, B3)`);
      }
    }

    // Validate date format
    if (sample['Freeze Date']) {
      const dateStr = String(sample['Freeze Date']);
      // Try to parse as date
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        sampleErrors.push(`Row ${rowNumber}: Invalid date format for "Freeze Date"`);
      }
    }

    if (sampleErrors.length === 0) {
      // Transform to internal format
      const validSample = {};
      Object.keys(columnMapping).forEach(excelCol => {
        const internalField = columnMapping[excelCol];
        let value = sample[excelCol];

        // Special handling for specific fields
        if (excelCol === 'Position') {
          value = String(value).trim().toUpperCase();
        } else if (excelCol === 'Freeze Date') {
          // Format date as YYYY-MM-DD
          const date = new Date(value);
          value = date.toISOString().split('T')[0];
        } else if (value !== undefined && value !== null) {
          value = String(value).trim();
        }

        validSample[internalField] = value || '';
      });

      validSamples.push(validSample);
    } else {
      errors.push(...sampleErrors);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
    validSamples
  };
};

/**
 * Generate sample import template
 * @param {string} filename - Optional filename
 */
export const downloadImportTemplate = (filename = 'sample_import_template') => {
  const templateData = [
    {
      'Name': 'Sample 1',
      'Position': 'A1',
      'Freeze Date': '2025-01-15',
      'Frozen By': 'Dr. Smith',
      'PI': 'Dr. Johnson',
      'Passage Number': 'P3',
      'Cap Color': 'red',
      'Notes': 'Example sample'
    },
    {
      'Name': 'Sample 2',
      'Position': 'A2',
      'Freeze Date': '2025-01-16',
      'Frozen By': 'Dr. Smith',
      'PI': 'Dr. Johnson',
      'Passage Number': 'P5',
      'Cap Color': 'blue',
      'Notes': 'Another example'
    }
  ];

  const worksheet = XLSX.utils.json_to_sheet(templateData);

  // Set column widths
  worksheet['!cols'] = [
    { wch: 20 }, // Name
    { wch: 10 }, // Position
    { wch: 12 }, // Freeze Date
    { wch: 15 }, // Frozen By
    { wch: 15 }, // PI
    { wch: 15 }, // Passage Number
    { wch: 12 }, // Cap Color
    { wch: 40 }  // Notes
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sample Import');

  const timestamp = new Date().toISOString().split('T')[0];
  const finalFilename = `${filename}_${timestamp}.xlsx`;

  XLSX.writeFile(workbook, finalFilename);
};
